'use client';
import {useEffect,useState,useSyncExternalStore} from "react";
import {createPortal} from "react-dom";
import Image from "next/image";
import {useLocale} from "../lib/i18n/use_locale";
import {loadingState} from "../lib/services/resource/loading-state";
import art from "./splash-art.json";
import {DEMO_BRAND,demoBrand} from "./branding";
import styles from "./boot-splash.module.css";

const megabytes=(bytes:number)=>(bytes/1048576).toFixed(1);
export default function BootSplash({preflight=false}:{preflight?:boolean}={}){
  const snapshot=useSyncExternalStore(loadingState.subscribe,loadingState.snapshot,loadingState.serverSnapshot);
  const state=preflight?loadingState.serverSnapshot():snapshot;
  const zh=useLocale()==="zh";
  const [mounted,setMounted]=useState(false),[preview,setPreview]=useState(false);
  useEffect(()=>{setMounted(true);setPreview(new URLSearchParams(window.location.search).get("splashPreview")==="1");},[]);
  if(state.phase==="ready"&&!preview)return null;
  const labels=zh?{
    checking:"正在检查资源…",graphics:"正在准备画面…",scene:"正在准备修理厂…",ready:"准备好了！",error:"加载暂停",
  }:{
    checking:"Checking your supplies…",graphics:"Warming up the graphics…",scene:"Getting the workshop ready…",ready:"Ready to play!",error:"Loading paused",
  };
  const splash=<section className={styles.screen} data-boot-splash data-phase={state.phase}
    aria-label={`${demoBrand.name} ${zh?"加载画面":"loading screen"}`} aria-busy={state.phase!=="error"&&state.phase!=="ready"}>
    <div className={styles.poster}>
      <Image src={art.hero} alt={zh?"Dive-eye、Snapshot 和 Hammerhead 摆出英雄姿势":"Dive-eye, Snapshot and Hammerhead in heroic comic poses"}
        fill sizes="(orientation: portrait) 100vw, 1120px" priority unoptimized className={styles.hero}/>
      {demoBrand.splashLogo && <Image src={demoBrand.splashLogo} alt={demoBrand.name} data-brand={DEMO_BRAND} width={demoBrand.splashWidth} height={demoBrand.splashHeight}
        priority unoptimized className={styles.brand}/>}
      <div className={styles.caption}>
        <p className={styles.status} role="status">{labels[state.phase]}</p>
        {state.phase==="error"?<>
          <p className={styles.error} role="alert">{state.error}</p>
          <button onClick={()=>window.location.reload()}>{zh?"重试":"TRY AGAIN"}</button>
        </>:<>
          <div className={styles.track} role="progressbar" aria-label={labels[state.phase]}
            aria-valuetext={state.phase==="graphics"?`${state.shaderDone} / ${state.shaderTotal}`:
              `${state.files} ${zh?"个资源已就绪":"files ready"}`}>
            <span data-ready={state.phase==="ready"}/>
          </div>
          <p className={styles.detail}>
            {state.phase==="graphics"&&state.shaderTotal>0?
              `${state.shaderDone} / ${state.shaderTotal} ${zh?"个画面程序已就绪":"graphics programs ready"}`:
              state.files>0?`${state.files} ${zh?"个资源已就绪":"files ready"}`:
                zh?"只下载有变化的资源":"Only changed files need downloading"}
            {state.cachedBytes>0&&<> · {megabytes(state.cachedBytes)} MB {zh?"来自本机":"from this device"}</>}
          </p>
          {!state.cacheAvailable&&<p className={styles.detail}>{zh?"浏览器存储不可用，将通过网络加载。":"Browser storage is unavailable; loading from the network."}</p>}
          {preview&&state.phase==="ready"&&<button onClick={()=>setPreview(false)}>{zh?"继续":"CONTINUE"}</button>}
        </>}
      </div>
    </div>
  </section>;
  // Server HTML supplies the first paint; the body portal then keeps the splash
  // above canvas containment and any body-mounted touch/gamepad controls.
  return mounted?createPortal(splash,document.body):splash;
}
